/**
 * @file detectBrowser.js
 * @fileOverview Checks browser type
 * @author Smartmatic
 * @creationDate 2019-09-18
 * @modificationDate 2025-03-14
 */
/**
 * Determines if the browser is Internet Explorer or Edge.
 *
 * This function utilises the 'userAgent' from the 'window.navigator' object to identify
 * whether the browser in use is Internet Explorer 10 or older, Internet Explorer 11, or
 * the Edge browser. This is particularly useful when certain CSS or JavaScript features
 * aren't supported in these browsers and may require special handling.
 * These string patterns ('MSIE ', 'Trident/', 'Edge/') in the 'userAgent' are used to
 * identify the specified browsers.
 *
 * @returns {boolean} A boolean indicating whether the current browser is IE, IE11, or Edge.
 */
function isBrowserEdgeOrIE() {
  const ua = window.navigator.userAgent;
  /* checking for IE 10 or older, IE 11, and Edge browsers, in that order: */
  return ua.includes('MSIE ') || ua.includes('Trident/') || ua.includes('Edge/');
}

/**
 * Checks if the browser being used is Internet Explorer.
 *
 * This function uses the userAgent property of the navigator object to determine whether the current
 * browser is an instance of Internet Explorer. It checks for versions 10 and older, as well as version
 * 11, by looking for the 'MSIE ' or 'Trident/' substrings respectively in the userAgent string.
 *
 * @returns {boolean} Returns true if the browser is detected to be an Internet Explorer version,
 * and false otherwise.
 */
function isBrowserIE() {
  const ua = window.navigator.userAgent;
  /* checking for IE 10 or older, IE 11 in that order: */
  return ua.includes('MSIE ') || ua.includes('Trident/');
}
/**
 * Checks if the current user agent denotes an Android device.
 *
 * This function can be used to handle specific scenarios where device based logic might be
 * required. It checks the "userAgent" property of the browser's navigator object to determine
 * whether the current device is Android or not.
 *
 * @returns {boolean} True if the user agent indicates an Android device, false otherwise.
 */
function isAndroid() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf('android') > -1;
}

/** ISB Client IS_MS_BROWSER Utility Component */
export const IS_MS_BROWSER = isBrowserEdgeOrIE();
export const IS_ANDROID = isAndroid();
export const IS_IE = isBrowserIE();
